import { Alert, Button, Flex, Modal, Typography, message } from 'antd';
import { useState } from 'react';

import { useUpdateBillingAutoRecharge } from '../../../api/generated/api';
import { useRefreshBillingContext } from '../hooks/useRefreshBillingContext';
import { apiErrorMessage, formatCredits, formatUsd, paymentMethodLabel } from '../shared/billing-format';
import { defaultPackId } from '../shared/packs';

import { PackSelect } from './PackSelect';

import type { GetBillingOverviewResponseDto } from '../../../api/generated/model/getBillingOverviewResponseDto';

const { Paragraph, Text } = Typography;

type Props = {
  open: boolean;
  organizationId: string;
  overview: GetBillingOverviewResponseDto;
  portalLoading: boolean;
  onOpenPortal: () => void;
  onClose: () => void;
};

/**
 * Consent, not a switch (UC-13): turning auto-recharge on authorises unattended charges, so the
 * manager picks the pack and confirms the exact sentence — threshold, card, price, credits and the
 * daily cap — before anything is saved. Turning it on below the threshold charges right away.
 */
export const AutoRechargeModal = ({
  open,
  organizationId,
  overview,
  portalLoading,
  onOpenPortal,
  onClose,
}: Props) => {
  const refresh = useRefreshBillingContext();
  const updateMutation = useUpdateBillingAutoRecharge();
  const { autoRecharge, topUp, paymentMethod } = overview;
  const packs = topUp?.packs ?? [];
  const [picked, setPicked] = useState<string | null>(null);
  const pack = packs.find(
    (candidate) => candidate.priceId === (picked ?? defaultPackId(packs, autoRecharge?.pack.priceId)),
  );

  const handleConfirm = async () => {
    if (!pack) return;
    try {
      await updateMutation.mutateAsync({ organizationId, data: { enabled: true, priceId: pack.priceId } });
      message.success('Auto-recharge is on');
      onClose();
      await refresh(organizationId);
    } catch (err) {
      message.error(apiErrorMessage(err, 'Failed to turn on auto-recharge. Please try again.'));
    }
  };

  return (
    <Modal
      title="Turn on auto-recharge"
      open={open}
      onCancel={onClose}
      okText="Turn on auto-recharge"
      onOk={handleConfirm}
      okButtonProps={{
        disabled: !paymentMethod || !pack || !autoRecharge,
        'data-testid': 'auto-recharge-confirm',
      }}
      confirmLoading={updateMutation.isPending}
      destroyOnHidden
    >
      <Flex vertical gap="middle" style={{ paddingTop: 8 }}>
        {!pack || !autoRecharge ? (
          <Alert type="info" showIcon title="Top-ups are not available right now." />
        ) : paymentMethod ? (
          <>
            <PackSelect packs={packs} value={pack.priceId} onChange={setPicked} testId="auto-recharge-pack" />
            <Paragraph style={{ margin: 0 }}>
              When your balance drops below{' '}
              <Text strong>{formatCredits(autoRecharge.thresholdCredits)} credits</Text>, we will charge{' '}
              <Text strong>
                {paymentMethodLabel(paymentMethod)} {formatUsd(pack.priceCents)}
              </Text>{' '}
              for {formatCredits(pack.credits)} credits, without asking again. At most{' '}
              <Text strong>{autoRecharge.dailyCap} times a day</Text>. You can turn this off any time.
            </Paragraph>
          </>
        ) : (
          <Alert
            type="warning"
            showIcon
            title="Add a card in Manage subscription first"
            action={
              <Button size="small" loading={portalLoading} onClick={onOpenPortal}>
                Manage subscription
              </Button>
            }
          />
        )}
      </Flex>
    </Modal>
  );
};
